import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useFirebase } from '#features/api/utils/firebase';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { registerNativePush } from '../utils/nativePush';
import { getPushPlatform } from '../utils/nativePlatform';
import { getPushDeviceSignal } from '../utils/deviceSettings';
import { addPushToken } from '../model/notifications';
import { useNotificationsStore } from '../model/notificationsStore';

export const usePushNotifications = () => {
  const firebaseClient = useFirebase();
  const notificationsStore = useNotificationsStore();
  const queryClient = useQueryClient();
  const addPushTokenMutation = useMutation({
    // Тип не перечисляется руками: снимок устройства растёт, и список, продублированный в трёх
    // местах, разъезжается на первой же правке.
    mutationFn: (input: Parameters<typeof addPushToken>[0]) => addPushToken(input),
  });

  const enableNotifications = useCallback(async () => {
    // if (notificationsStore.status === 'ENABLED') {
    //   return;
    // }

    // Native shell: register via the Capacitor plugin (not the web SW) and tag the
    // token with the real platform — ANDROID → FCM notification, IOS → direct APNs —
    // so the backend delivers an OS-rendered notification even when the app is closed.
    if (envConfig.IS_NATIVE) {
      const token = await registerNativePush();
      if (!token) {
        notificationsStore.setStatus('DISABLED');
        return;
      }
      try {
        const platform = await getPushPlatform();
        const device = await getPushDeviceSignal();
        const data = await addPushTokenMutation.mutateAsync({ token, platform, ...device });
        if (data?.__typename === 'Operation') {
          localStorage.setItem('fcm_token', token);
          notificationsStore.setStatus('ENABLED');
          queryClient.invalidateQueries({ queryKey: ['me'] });
        } else {
          console.warn('[push] native addPushToken returned non-Operation result', data);
        }
      } catch (err) {
        console.warn('[push] native addPushToken mutation failed', err);
      }
      return;
    }

    const registerNotifications = async () => {
      if (!firebaseClient) {
        console.warn('[push] firebase client unavailable — push not enabled');
        notificationsStore.setStatus('DISABLED');
        return;
      }

      let token: string | null = null;
      try {
        token = await firebaseClient.getPushToken();
      } catch (err) {
        console.warn('[push] getPushToken threw', err);
      }

      if (!token) {
        console.warn('[push] getPushToken returned no token (FCM/SW init failed?)');
        notificationsStore.setStatus('DISABLED');
        return;
      }

      try {
        const data = await addPushTokenMutation.mutateAsync({ token, platform: 'WEB' });
        if (data?.__typename === 'Operation') {
          localStorage.setItem('fcm_token', token);
          notificationsStore.setStatus('ENABLED');
          // Refresh `useProfile()` so consumers (e.g. BrowserPushStatus banner)
          // re-render once the new token is reflected in profile.pushToken[].
          queryClient.invalidateQueries({ queryKey: ['me'] });
        } else {
          console.warn('[push] addPushToken returned non-Operation result', data);
        }
      } catch (err) {
        console.warn('[push] addPushToken mutation failed', err);
      }
    };

    if (!('Notification' in window)) {
      console.log('Notifications are not supported');
    } else if (Notification.permission === 'granted') {
      console.log('Permission already granted');
      registerNotifications();
    } else if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        registerNotifications();
      }
    }
  }, [addPushTokenMutation, firebaseClient, notificationsStore, queryClient]);

  const disableNotifications = useCallback(async () => {
    if (firebaseClient) {
      await firebaseClient.deletePushToken();
    }
    notificationsStore.setStatus('DISABLED');
  }, [firebaseClient, notificationsStore]);

  return {
    enableNotifications,
    disableNotifications,
    status: notificationsStore.status,
    isEnabling: addPushTokenMutation.isPending,
  };
};
